import type { Asset, AssetKind } from '@avs/contract';
import { apiClient, API_MODE } from '../api/client.js';

/**
 * 三步上传（Slice 8 起：R2 via Worker，去掉火山 SigV4）：
 *   1) BFF.requestUpload  → 拿 assetId + uploadUrl（指向 Worker 的 R2 直写路由）
 *   2) 浏览器 PUT 文件到 uploadUrl（同源，无签名无 CORS；mock 模式跳过）
 *   3) BFF.confirmUpload  → 把 asset 落库
 */

export type UploadOk = { ok: true; asset: Asset };
export type UploadErr = { ok: false; error: string };
export type UploadResult = UploadOk | UploadErr;

export async function uploadOneFile(file: File, kind: AssetKind): Promise<UploadResult> {
  // 1) 申请 assetId + uploadUrl
  const up = await apiClient.requestUpload({
    kind,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
  }).catch((e: unknown) => ({ _error: e }));
  if ('_error' in up) {
    return { ok: false, error: extractMsg(up._error, '申请上传失败') };
  }

  // 2) real 模式：PUT 文件到 Worker（写 R2）；mock 模式跳过
  if (API_MODE === 'real' && up.uploadUrl && !up.uploadUrl.startsWith('mock://')) {
    try {
      const res = await fetch(up.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        return { ok: false, error: `上传失败 ${res.status}：${text.slice(0, 200)}` };
      }
    } catch (e) {
      return { ok: false, error: extractMsg(e, '上传网络错误') };
    }
  }

  // 3) confirm
  const asset = await apiClient.confirmUpload({
    assetId: up.assetId, sizeBytes: file.size,
  }).catch((e: unknown) => ({ _error: e }));
  if ('_error' in asset) {
    return { ok: false, error: extractMsg(asset._error, '上传确认失败') };
  }

  return { ok: true, asset };
}

function extractMsg(e: unknown, fallback: string): string {
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}
